/**
 * Transform DSL Generator
 *
 * Direct port from Vue frontend — framework-agnostic TypeScript.
 * Generates Solidafy DSL code from a visual transform graph.
 */

import { getNodeSpec } from './transform-nodes';
import type { TransformNode, TransformEdge } from '@/types/transform';

interface GenerationContext {
  nodeById: Map<string, TransformNode>;
  outgoing: Map<string, TransformEdge[]>;
  incoming: Map<string, TransformEdge[]>;
  nodeVariable: Map<string, string>;
  lines: string[];
  visited: Set<string>;
}

export function generateDsl(nodes: TransformNode[], edges: TransformEdge[]): string {
  if (nodes.length === 0) return '// No nodes in transform';

  const ctx: GenerationContext = {
    nodeById: new Map(nodes.map((n) => [n.id, n])),
    outgoing: new Map(),
    incoming: new Map(),
    nodeVariable: new Map(),
    lines: [],
    visited: new Set(),
  };

  edges.forEach((e) => {
    if (!ctx.outgoing.has(e.sourceId)) ctx.outgoing.set(e.sourceId, []);
    ctx.outgoing.get(e.sourceId)!.push(e);
    if (!ctx.incoming.has(e.targetId)) ctx.incoming.set(e.targetId, []);
    ctx.incoming.get(e.targetId)!.push(e);
  });

  const sources = nodes.filter((n) => n.type === 'source');
  if (sources.length === 0) return '// No source nodes found. Add a source node to start.';

  sources.forEach((source) => {
    const spec = getNodeSpec(source.type);
    if (!spec) return;
    const varName = (source.config?.variable as string) || source.variable || `src_${source.id.slice(0, 4)}`;
    ctx.nodeVariable.set(source.id, varName);
    ctx.visited.add(source.id);
    ctx.lines.push(spec.generateDsl(source.config));
  });

  const queue: TransformEdge[] = [];
  sources.forEach((s) => {
    const outEdges = ctx.outgoing.get(s.id) || [];
    outEdges.forEach((e) => queue.push(e));
  });

  const maxIterations = nodes.length * 2;
  let iterations = 0;

  while (queue.length > 0 && iterations < maxIterations) {
    iterations++;
    const edge = queue.shift()!;
    const node = ctx.nodeById.get(edge.targetId);

    if (!node || ctx.visited.has(node.id)) continue;

    const inputs = ctx.incoming.get(node.id) || [];
    const allInputsReady = inputs.every((e) => ctx.visited.has(e.sourceId));

    if (!allInputsReady) {
      queue.push(edge);
      continue;
    }

    ctx.visited.add(node.id);

    const sortedInputs = [...inputs].sort((a, b) => a.targetPort.localeCompare(b.targetPort));
    const inputVars = sortedInputs.map((e) => ctx.nodeVariable.get(e.sourceId)!).filter(Boolean);

    if (inputVars.length === 0 && node.type !== 'source') continue;

    const spec = getNodeSpec(node.type);
    if (!spec) continue;

    let expr: string;

    if (node.type === 'join' || node.type === 'cdc') {
      expr = spec.generateDsl(node.config, inputVars[0] || 'unknown', inputVars[1] || 'unknown');
    } else if (node.type === 'output') {
      expr = spec.generateDsl(node.config, inputVars[0]);
      ctx.lines.push(expr);
      continue;
    } else {
      expr = spec.generateDsl(node.config, inputVars[0]);
    }

    const outEdges = ctx.outgoing.get(node.id) || [];
    const needsVariable = shouldCreateVariable(node, outEdges, ctx);

    if (needsVariable) {
      const varName = generateVariableName(node, ctx);
      ctx.nodeVariable.set(node.id, varName);
      ctx.lines.push(`${varName} = ${expr}`);
    } else if (outEdges.length > 0) {
      ctx.nodeVariable.set(node.id, expr);
    } else {
      ctx.nodeVariable.set(node.id, expr);
      ctx.lines.push(expr);
    }

    outEdges.forEach((e) => queue.push(e));
  }

  if (ctx.lines.length > 0) {
    const lastLine = ctx.lines[ctx.lines.length - 1];
    if (lastLine.includes(' = ') && !lastLine.startsWith('(')) {
      const varName = lastLine.split(' = ')[0].trim();
      ctx.lines.push(varName);
    }
  }

  return ctx.lines.join('\n');
}

function shouldCreateVariable(node: TransformNode, outEdges: TransformEdge[], ctx: GenerationContext): boolean {
  if (outEdges.length > 1) return true;

  if (outEdges.length === 1) {
    const targetNode = ctx.nodeById.get(outEdges[0].targetId);
    if (targetNode && (targetNode.type === 'join' || targetNode.type === 'cdc')) return true;
  }

  const currentVar = ctx.nodeVariable.get(node.id) || '';
  if (currentVar.includes('.') && currentVar.split('.').length > 3) return true;

  return false;
}

function generateVariableName(node: TransformNode, ctx: GenerationContext): string {
  if (node.config?.variable) return node.config.variable as string;

  const prefix = getVariablePrefix(node.type);
  const shortId = node.id.slice(0, 4);
  let varName = `${prefix}_${shortId}`;
  let counter = 1;
  while (Array.from(ctx.nodeVariable.values()).includes(varName)) {
    varName = `${prefix}_${shortId}_${counter++}`;
  }
  return varName;
}

function getVariablePrefix(type: string): string {
  const prefixes: Record<string, string> = {
    source: 'src',
    filter: 'filtered',
    select: 'selected',
    drop: 'dropped',
    dedupe: 'deduped',
    sort: 'sorted',
    limit: 'limited',
    rename: 'renamed',
    cast: 'casted',
    add: 'computed',
    with_columns: 'enriched',
    join: 'joined',
    group_by: 'grouped',
    window: 'windowed',
    pivot: 'pivoted',
    unpivot: 'unpivoted',
    validate: 'validated',
    scd2: 'scd',
    cdc: 'cdc',
  };
  return prefixes[type] || 'step';
}

export function generateDslToNode(nodes: TransformNode[], edges: TransformEdge[], targetNodeId: string): string {
  const requiredNodes = new Set<string>();
  const queue = [targetNodeId];

  const incoming = new Map<string, TransformEdge[]>();
  edges.forEach((e) => {
    if (!incoming.has(e.targetId)) incoming.set(e.targetId, []);
    incoming.get(e.targetId)!.push(e);
  });

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (requiredNodes.has(nodeId)) continue;
    requiredNodes.add(nodeId);
    const inEdges = incoming.get(nodeId) || [];
    inEdges.forEach((e) => queue.push(e.sourceId));
  }

  const filteredNodes = nodes.filter((n) => requiredNodes.has(n.id));
  const filteredEdges = edges.filter((e) => requiredNodes.has(e.sourceId) && requiredNodes.has(e.targetId));

  return generateDsl(filteredNodes, filteredEdges);
}

export function validateGraph(nodes: TransformNode[], edges: TransformEdge[]): string[] {
  const errors: string[] = [];

  if (nodes.length === 0) {
    errors.push('No nodes in transform');
    return errors;
  }

  const sources = nodes.filter((n) => n.type === 'source');
  if (sources.length === 0) errors.push('No source nodes found');

  const outputs = nodes.filter((n) => n.type === 'output');
  if (outputs.length === 0) errors.push('No output nodes found');

  const incoming = new Map<string, TransformEdge[]>();
  const outgoing = new Map<string, TransformEdge[]>();
  edges.forEach((e) => {
    if (!incoming.has(e.targetId)) incoming.set(e.targetId, []);
    incoming.get(e.targetId)!.push(e);
    if (!outgoing.has(e.sourceId)) outgoing.set(e.sourceId, []);
    outgoing.get(e.sourceId)!.push(e);
  });

  nodes.forEach((node) => {
    const spec = getNodeSpec(node.type);
    if (!spec) {
      errors.push(`Unknown node type: ${node.type}`);
      return;
    }

    const inEdges = incoming.get(node.id) || [];
    if (spec.inputs > 0 && inEdges.length < spec.inputs) {
      errors.push(`Node "${spec.label}" (${node.id.slice(0, 8)}) is missing inputs`);
    }

    if (!node.configured) {
      spec.fields.forEach((field) => {
        if (field.required && !node.config?.[field.name]) {
          errors.push(`Node "${spec.label}" is missing required field: ${field.label}`);
        }
      });
    }

    if (node.type !== 'source' && inEdges.length === 0) {
      errors.push(`Node "${spec.label}" has no input connection`);
    }
  });

  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycle(nodeId: string): boolean {
    if (recursionStack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    recursionStack.add(nodeId);
    const outEdges = outgoing.get(nodeId) || [];
    for (const edge of outEdges) {
      if (hasCycle(edge.targetId)) return true;
    }
    recursionStack.delete(nodeId);
    return false;
  }

  for (const node of nodes) {
    if (hasCycle(node.id)) {
      errors.push('Cycle detected in transform graph');
      break;
    }
  }

  return errors;
}

export function getExecutionOrder(nodes: TransformNode[], edges: TransformEdge[]): string[] {
  const order: string[] = [];
  const visited = new Set<string>();

  const incoming = new Map<string, Set<string>>();
  const outgoing = new Map<string, Set<string>>();

  nodes.forEach((n) => {
    incoming.set(n.id, new Set());
    outgoing.set(n.id, new Set());
  });

  edges.forEach((e) => {
    incoming.get(e.targetId)?.add(e.sourceId);
    outgoing.get(e.sourceId)?.add(e.targetId);
  });

  const queue = nodes.filter((n) => incoming.get(n.id)?.size === 0).map((n) => n.id);

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    order.push(nodeId);

    outgoing.get(nodeId)?.forEach((targetId) => {
      incoming.get(targetId)?.delete(nodeId);
      if (incoming.get(targetId)?.size === 0) {
        queue.push(targetId);
      }
    });
  }

  return order;
}

// Re-export types used by consumers
export type { TransformNode, TransformEdge } from '@/types/transform';
